import { z } from 'zod'

export const CRM_ACTIVITY_CONTRACT_VERSION = '1.0.0' as const

const optionalQueryString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional(),
)

/** Query keys accepted by every CRM activity read alias. Domain validation stays
 * in the CRM route so existing error precedence and positive-integer rules are
 * unchanged. */
export const crmActivityReadQuerySchema = z.object({
  orgId: optionalQueryString,
  org_id: optionalQueryString,
  leadId: optionalQueryString,
  actorUserId: optionalQueryString,
  page: optionalQueryString,
  offset: optionalQueryString,
  limit: optionalQueryString,
})

export const crmActivitySummarySchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  leadId: z.string().min(1),
  createdByUserId: z.string().min(1),
  assigneeUserId: z.string().nullable(),
  activityType: z.string().min(1),
  body: z.string(),
  occurredAt: z.string().nullable(),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough()

export const crmActivityReadEvidenceSchema = z.object({
  sourceOfTruth: z.literal('DB1'),
  tablesRead: z.tuple([z.literal('crm_activities')]),
  projection: z.literal('crm-activity-list'),
  rawPayloadReturned: z.literal(false),
  rawActivityBodyReturned: z.literal(false),
}).passthrough()

export const crmActivityReadSideEffectsSchema = z.object({
  db1Read: z.boolean(),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

export const crmActivityListResponseSchema = z.object({
  contract_version: z.literal(CRM_ACTIVITY_CONTRACT_VERSION),
  activities: z.array(crmActivitySummarySchema),
  activityEvidence: crmActivityReadEvidenceSchema,
  sideEffects: crmActivityReadSideEffectsSchema,
}).passthrough()

export const CRM_ACTIVITY_ERROR_CODES = [
  'CR_VALIDATION_FAILED',
  'CR_PERMISSION_DENIED',
  'CR_AUTH_UNAVAILABLE',
  'CR_MEMBERSHIP_UNAVAILABLE',
  'CR_DOMAIN_UNAVAILABLE',
] as const

export const crmActivityErrorEnvelopeSchema = z.object({
  contract_version: z.string().min(1),
  error: z.object({
    code: z.enum(CRM_ACTIVITY_ERROR_CODES),
    message: z.string().min(1),
    reference: z.string().min(1),
    details: z.record(z.unknown()).optional(),
    request_id: z.string().optional(),
  }).passthrough(),
}).passthrough()

export type CrmActivityReadQuery = z.infer<typeof crmActivityReadQuerySchema>
export type CrmActivitySummary = z.infer<typeof crmActivitySummarySchema>
export type CrmActivityReadEvidence = z.infer<typeof crmActivityReadEvidenceSchema>
export type CrmActivityReadSideEffects = z.infer<typeof crmActivityReadSideEffectsSchema>
export type CrmActivityListResponse = z.infer<typeof crmActivityListResponseSchema>
export type CrmActivityErrorCode = (typeof CRM_ACTIVITY_ERROR_CODES)[number]
export type CrmActivityErrorEnvelope = z.infer<typeof crmActivityErrorEnvelopeSchema>
